import React, { useMemo, useState, useCallback, useContext } from 'react';
import styled from 'styled-components';
import usePublications from '@ubeya/shared/hooks/account/usePublications';
import {
  Modal,
  FlexMiddle,
  Header2SemiBold,
  Header2,
  ButtonHeader as BaseButtonHeader,
  Header as BaseHeader,
  FlexColumn,
  Table,
  NameRenderer,
  Search,
  CheckedTimeAgoWithTime,
  CircleIcon
} from '@ubeya/shared-web/components';
import { media, responsiveShow } from '@ubeya/shared-web/style';
import useTableSort from '@ubeya/shared/hooks/useTableSort';
import { filterUsers } from '@ubeya/shared/utils/filters';
import useFormattingMap from '@ubeya/shared-web/hooks/useFormattingMap';
import ConfigContext from '@ubeya/shared-web/contexts/ConfigContext';
import { ReactComponent as BaseExcelIcon } from '@ubeya/shared-web/assets/file-excel.svg';
import { PublishSettingsModal } from '../Publish';
import EmployeeCard from '../EmployeeCard/EmployeeCard';

const Type = styled(Header2SemiBold)`
  margin-right: 20px;
`;

const Header = styled(BaseHeader).attrs(() => ({ combined: true }))`
  ${({ showHeaderOnMobile }) => !showHeaderOnMobile && `${responsiveShow.mobile}`}
  > div {
    padding: 17px 40px 0 40px;
    ${media.mobile`padding: 17px 40px;`}

    > div {
      flex-wrap: wrap-reverse;
      ${media.mobile`flex-wrap: nowrap;`}
      padding: 0;
      > div {
        flex-wrap: wrap;
      }
    }
  }
`;

const ButtonHeader = styled(BaseButtonHeader)`
  margin-right: 20px;
  margin-bottom: 9px;
  margin-top: 9px;
  padding: 10px;
  ${media.mobile`margin-bottom: 0; margin-top: 0;`}
`;

const Container = styled(FlexColumn)`
  flex: 1;
  ${media.tablet`padding:0 42px 34px 42px;`}
`;

const StatsModal = ({
  publicationIds,
  additionalColumns = [],
  injectColumns = [],
  showExpand = true,
  title: insideTitle,
  showPrompt = false,
  showHeaderOnMobile,
  onPublish,
  onClose,
  consolidate = false,
  isShiftPaused = false,
  onExport
}) => {
  const { t } = useContext(ConfigContext);

  const { isLoading, publicationsMap } = usePublications({
    publicationIds
  });

  const [selectedEmployee, setSelectedEmployee] = useState();
  const [search, setSearch] = useState('');

  const formattingMap = useFormattingMap();

  const columns = useMemo(
    () => [
      {
        Header: t('name'),
        dataKey: 'name',

        Cell: NameRenderer,
        columnData: ({ rowData }) => ({
          withUnderline: true,
          onClick: () => setSelectedEmployee({ ...rowData, id: rowData?.employeeId })
        }),
        sortable: true,
        format: 'user'
      },
      ...(injectColumns || []),
      {
        Header: t('seen'),
        dataKey: 'seen',
        Cell: CheckedTimeAgoWithTime,
        sortable: true,
        format: 'boolean'
      },
      ...additionalColumns
    ],
    [t, injectColumns, additionalColumns]
  );

  const users = useMemo(() => {
    const usersMap = Object.keys(publicationsMap).reduce((output, publicationId) => {
      const publication = publicationsMap[publicationId];
      const publicationUsers = publication.users;

      //publicationUsers is from all publications, so we may run over the same users
      // in different publication

      publicationUsers.forEach((publicationUser) => {
        if (!output[publicationUser.id]) {
          output[publicationUser.id] = publicationUser;
          return;
        }
        //at least in one publication, user is engaged - set to the value
        if (publicationUser.engaged) {
          output[publicationUser.id].engaged = publicationUser.engaged;
        }

        //at least in one publication, user is seen - set to the value
        if (publicationUser.seen) {
          output[publicationUser.id].seen = publicationUser.seen;
        }

        //at least in one publication, user is booked - set to the value
        if (publicationUser.booked) {
          output[publicationUser.id].booked = publicationUser.booked;
        }
      });
      return output;
    }, {});
    return Object.values(usersMap);
  }, [publicationsMap]);

  const seenUsers = useMemo(() => users?.filter(({ seen, booked }) => !!seen || !!booked), [users]);
  const answeredUsers = useMemo(() => users?.filter(({ engaged, booked }) => !!engaged || !!booked), [users]);

  const title = useMemo(
    () => (
      <FlexMiddle>
        <Type>{t(insideTitle)}</Type>

        <Header2>
          {t('viewableBy', { total: formattingMap.numberWithComma(users.length) })} |{' '}
          {t('seenBy', { total: formattingMap.numberWithComma(seenUsers.length) })}
          {users.length > 0 ? `(${Math.round((seenUsers.length * 100) / users.length)}%)` : ''}
        </Header2>
      </FlexMiddle>
    ),
    [t, insideTitle, formattingMap, users.length, seenUsers.length]
  );

  const [notificationConditions, setNotificationConditions] = useState();

  const [sort, setSort] = useState({ sortBy: 'seen', sortDirection: 'asc' });
  const sortedData = useTableSort({ data: users, columns, ...sort });

  const filteredData = useMemo(() => (search ? filterUsers(sortedData, search) : sortedData), [search, sortedData]);

  const publishToNotSeen = useCallback(() => setNotificationConditions({ seen: false }), []);

  const expendAudience = useCallback(() => (showPrompt ? onPublish(publishToNotSeen) : onPublish()), [
    onPublish,
    publishToNotSeen,
    showPrompt
  ]);

  const totalNotAnswered = users.length - answeredUsers.length;

  return (
    <>
      <Modal
        usePortal
        onClose={onClose}
        title={title}
        showConfirmButton={false}
        isPaddingContent={false}
        isScrollableContent={false}>
        <Header
          showHeaderOnMobile={showHeaderOnMobile}
          leftSide={
            <>
              <ButtonHeader onClick={() => !isShiftPaused && publishToNotSeen()} disabled={isShiftPaused}>
                {t('publishNotSeen', { total: formattingMap.numberWithComma(users.length - seenUsers.length) })}
              </ButtonHeader>
              {(insideTitle === 'question' || insideTitle === 'shifts') && (
                <ButtonHeader
                  onClick={() =>
                    !isShiftPaused && totalNotAnswered > 0 && setNotificationConditions({ engaged: false })
                  }
                  disabled={isShiftPaused || 0 === totalNotAnswered}>
                  {t('publishNotAnswered', {
                    total: formattingMap.numberWithComma(totalNotAnswered)
                  })}
                </ButtonHeader>
              )}
              {showExpand && <ButtonHeader onClick={expendAudience}>{t('expandPublish')}</ButtonHeader>}
            </>
          }
          rightSide={
            <>
              {!!onExport && filteredData.length > 0 && (
                <CircleIcon icon={BaseExcelIcon} onClick={() => onExport(filteredData)} />
              )}
              <Search value={search} onChange={setSearch} />
            </>
          }
        />

        <Container>
          <Table
            columns={columns}
            data={filteredData}
            isLoading={isLoading}
            shadow={false}
            isVirtualized
            {...sort}
            setSort={setSort}
          />
        </Container>
      </Modal>

      {notificationConditions && (
        <PublishSettingsModal
          toAddMessage
          onClose={() => setNotificationConditions()}
          publicationIds={publicationIds}
          conditions={notificationConditions}
          consolidate={consolidate}
        />
      )}

      {selectedEmployee && <EmployeeCard defaultData={selectedEmployee} onClose={() => setSelectedEmployee()} />}
    </>
  );
};

export default StatsModal;
